import React, { useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ProfilePicture, Tooltip } from '@getro/rombo';
import { Box, Flex } from 'rebass/styled-components';
import { useSelector } from 'react-redux';
import { Plus } from 'lucide-react';

import { networksSelector } from '../../../redux/selectors';
import { UserAndHelpMenu } from '../userAndHelpMenu';
import { trackEvent } from '../../../helpers/analytics';
import { useResetCache } from '../../../hooks/useResetCache';

export const NetworkNav = () => {
  const { networks, active } = useSelector(networksSelector);
  const resetCache = useResetCache();
  const { url: jobBoardUrl, logoUrl: logo, isManager, ...rest } = networks[active];
  const networkList = Object.values(networks).sort((n1, n2) => {
    const n1Label = n1.name || n1.domain;
    const n2Label = n2.name || n2.domain;

    return n1Label.localeCompare(n2Label);
  });
  const onClickNetwork = () => {
    resetCache();
  };
  const scrollableBoxRef = useRef(null);

  useEffect(() => {
    const scrollableBox = scrollableBoxRef.current;
    const handleScroll = () => {
      sessionStorage.setItem('networkNavScrollPosition', scrollableBox.scrollTop.toString());
    };

    if (scrollableBox) {
      const scrollPosition = sessionStorage.getItem('networkNavScrollPosition');
      if (scrollPosition) {
        scrollableBox.scrollTop = parseInt(scrollPosition, 10);
      }

      scrollableBox.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableBox) {
        scrollableBox.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        bg: 'neutral.20',
        borderRight: '1px solid',
        borderRightColor: 'neutral.100',
        height: '100vh',
      }}
    >
      <Box
        maxWidth="48px"
        sx={{
          overflow: 'scroll',
          height: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          px: '8px',
          py: '8px',
          top: 0,
          bottom: 0,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        ref={scrollableBoxRef}
      >
        {networkList.map(({ id, slug, name, domain, logoUrl }) => (
          <Tooltip
            key={id}
            placement="right"
            content={name}
            sx={{
              width: 'max-content',
              p: '4px 8px',
            }}
            trigger={
              <Flex
                key={id}
                as={NavLink}
                to={`/networks/${slug}`}
                height="32px"
                alignItems="center"
                sx={{
                  background: 'none',
                  textDecoration: 'none',
                  transition: 'all ease 0.3s',
                  boxShadow:
                    rest.id === id
                      ? '0px 0px 0px 2px #F7FAFC, 0px 0px 0px 4px #6D33FF'
                      : ' 0px 0px 0px 2px #FFFFFF, 0px 0px 0px 3px #DEE4ED',
                  borderRadius: '4px',
                  ':hover': {
                    bg: 'purple.100',
                    boxShadow:
                      rest.id === id
                        ? '0px 0px 0px 2px #F7FAFC, 0px 0px 0px 4px #6D33FF'
                        : '0px 0px 0px 2px #F7FAFC, 0px 0px 0px 4px #8A75F0',
                  },
                  '& > svg': {
                    color: 'purple.500',
                  },
                }}
                onClick={onClickNetwork}
                justifyContent="space-between"
              >
                <Flex alignItems="center">
                  <Box borderRadius="4px" width="32px" height="32px" minWidth="32px" minHeight="32px">
                    <ProfilePicture
                      imageUrl={logoUrl}
                      name={name || domain}
                      variant="square"
                      width="32px"
                      height="32px"
                      minWidth="32px"
                      minHeight="32px"
                    />
                  </Box>
                </Flex>
              </Flex>
            }
          />
        ))}
        <Tooltip
          placement="right"
          content="Create network"
          sx={{
            width: 'max-content',
            p: '4px 8px',
          }}
          trigger={
            <Box
              sx={{
                cursor: 'pointer',
                bg: 'neutral.50',
                borderRadius: '4px',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                minHeight: '32px',
                display: 'flex',
              }}
              as="a"
              href=" https://www.getro.com/demo"
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                trackEvent('create_network_click', {
                  network_id: active,
                  origin: 'network_selector',
                });
              }}
            >
              <Box as={Plus} size="16px" />
            </Box>
          }
        />
      </Box>

      <Box
        sx={{
          position: 'fixed',
          height: '80px',
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '48px',
          gap: '8px',
        }}
      >
        <UserAndHelpMenu
          minimal
          helpTooltipContent="Help and support"
          myAccountTooltipContent="My account"
          profilePictureSize="32px"
        />
      </Box>
    </Box>
  );
};
